import React, { useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  FormGroup,
  Form,
  Label,
  Input,
  Col,
  Row,
} from "reactstrap";

import zxcvbn from 'zxcvbn';
import Logo from 'components/Logo';
import AuthFooter from 'components/Footers/AuthFooter';
import AuthAnimation from './AuthAnimation';
import { register } from 'network/ApiAxios';
import { useSettings } from "contexts/SettingsContext";
import { ROUTES } from "core/links";

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [password, setPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { settings, isLoading: isSettingsLoading, hasError: settingsError } = useSettings();
  const isRegistrationActive = !settingsError && settings?.is_registeration_disabled !== "yes";

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    const evaluation = zxcvbn(newPassword);
    switch (evaluation.score) {
      case 0:
      case 1:
        setPasswordStrength('weak');
        break;
      case 2:
        setPasswordStrength('fair');
        break;
      case 3:
        setPasswordStrength('good');
        break;
      case 4:
        setPasswordStrength('strong');
        break;
      default:
        setPasswordStrength('');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    let newErrors = {};

    if (!name.trim()) {
      newErrors.name = "Full Name is required.";
    }
    if (!email.trim()) {
      newErrors.email = "Email is required.";
    }
    if (!password.trim()) {
      newErrors.password = "Password is required.";
    }
    if (!jobTitle.trim()) {
      newErrors.jobTitle = "Job Title is required.";
    } else if (jobTitle.trim().length < 2) {
      newErrors.jobTitle = "Job Title must be at least 2 characters.";
    }
    if (!organisation.trim()) {
      newErrors.organisation = "Organisation is required.";
    } else if (organisation.trim().length < 2) {
      newErrors.organisation = "Organisation must be at least 2 characters.";
    }
    const mobileDigits = mobileNumber.replace(/\D/g, '');
    if (!mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile Number is required.";
    } else if (!/^\+?[\d\s\-().]+$/.test(mobileNumber.trim()) || mobileDigits.length < 7 || mobileDigits.length > 15) {
      newErrors.mobileNumber = "Enter a valid mobile number with 7 to 15 digits, e.g. +44 7911 123456.";
    }
    if (!termsAccepted) {
      newErrors.terms = "You must agree to the terms and conditions to register.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const data = await register(name, email, password, jobTitle.trim(), organisation.trim(), mobileNumber.trim());
      if (data.success === false) {
        setSuccessMessage('');
        setErrors({ form: data.message });
      } else {
        setErrors({});
        setSuccessMessage(data.message);
      }
    }
    setIsLoading(false);
  };


  if (isSettingsLoading) {
    return <div className="text-center">Loading...</div>;
  }

  return (
    <>
      <Col xl="5" lg="5" md="8" className="d-flex flex-column mx-lg-0 mx-auto">
        <Logo />
        {isRegistrationActive ? (
          <Card className="card-plain register-card">
            <CardHeader className="pb-0 text-left">
              <h4 className="font-weight-bolder">Sign Up</h4>
            </CardHeader>
            <CardBody className="py-1">
              <Form role="form" className="register-form" onSubmit={handleRegister}>
                <FormGroup className="register-field">
                  <Label className="mb-1 ms-0" style={{ marginLeft: 0 }}>Full Name</Label>
                  <Input
                    className="mb-0" name='full_name'
                    placeholder="Enter first & last name" type="text"
                    onChange={e => setName(e.target.value)}
                  />
                  {errors.name && <div className="text-danger">{errors.name}</div>}
                </FormGroup>
                <FormGroup className="register-field">
                  <Label className="mb-1 ms-0" style={{ marginLeft: 0 }}>Email</Label>
                  <Input className="mb-0"
                    placeholder="Enter valid email address"
                    name='email'
                    type="email"
                    autoComplete="new-email"
                    onChange={e => setEmail(e.target.value)}
                  />
                  {errors.email && <div className="text-danger">{errors.email}</div>}
                </FormGroup>
                <Row className="gx-2">
                  <Col md="6">
                    <FormGroup className="register-field">
                      <Label className="mb-1 ms-0" style={{ marginLeft: 0 }}>Job Title *</Label>
                      <Input className="mb-0"
                        placeholder="Enter job title"
                        type="text"
                        onChange={e => setJobTitle(e.target.value)}
                      />
                      {errors.jobTitle && <div className="text-danger">{errors.jobTitle}</div>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="register-field">
                      <Label className="mb-1 ms-0" style={{ marginLeft: 0 }}>Organisation *</Label>
                      <Input className="mb-0"
                        placeholder="Organisation"
                        type="text"
                        onChange={e => setOrganisation(e.target.value)}
                      />
                      {errors.organisation && <div className="text-danger">{errors.organisation}</div>}
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="gx-2">
                  <Col md="6">
                    <FormGroup className="register-field">
                      <Label className="mb-1 ms-0" style={{ marginLeft: 0 }}>Mobile Number *</Label>
                      <Input className="mb-0"
                        placeholder="+44 7911 123456"
                        type="tel"
                        autoComplete="tel"
                        onChange={e => setMobileNumber(e.target.value)}
                      />
                      {errors.mobileNumber && <div className="text-danger">{errors.mobileNumber}</div>}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup className="register-field">
                      <Label className="mb-1 ms-0" style={{ marginLeft: 0 }}>Password</Label>
                      <Input className="mb-0"
                        placeholder="Strong password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={handlePasswordChange}
                      />
                      {errors.password && <div className="text-danger">{errors.password}</div>}
                    </FormGroup>
                  </Col>
                </Row>
                <div className="text-muted font-italic mb-2">
                  <small>
                    Password strength: <span className={`text-${passwordStrength === 'strong' ? 'success' : 'danger'} font-weight-700`}>{passwordStrength}</span>
                  </small>
                  <p className="text-xs mb-0">
                    Min 6 characters, a number, an uppercase letter & a special character.
                  </p>
                </div>
                <div className="form-check form-check-info text-left">
                  <input
                    className="form-check-input"
                    id="checkRegister"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={() => setTermsAccepted(!termsAccepted)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="checkRegister"
                  >
                    <span className="text-muted">
                      I agree with the{" "}
                      <a href={ROUTES.TERMS} className="text-dark font-weight-bolder">
                        Terms and Conditions
                      </a>
                    </span>
                  </label>
                </div>
                {errors.terms && <div className="text-danger">{errors.terms}</div>}
                {errors.form && <div className="text-danger">{errors.form}</div>}
                {successMessage && <div className="text-success">{successMessage}</div>}
                <div className="text-center">
                  <Button type="submit" className="w-100 mt-4 mb-0" color="custom">
                    {isLoading ? <FaSpinner className="icon-spin" /> : 'Sign up'}
                  </Button>
                </div>
              </Form>
            </CardBody>
            <CardFooter className="text-center pt-0 px-sm-4 px-1">
              <p className="mb-4 mx-auto">
                Already have an account?
                <a href={ROUTES.LOGIN} className="text-primary font-weight-bold"> Sign in</a>
              </p>
            </CardFooter>
          </Card>
        ) : (
          <Card className="card text-center my-5 shadow-none">
            <div className="overflow-hidden position-relative border-radius-lg bg-cover h-100" >
              <span className="mask bg-none"></span>
              <div className="card-body position-relative z-index-1 h-100 p-5">
                <h6 className="text-warning font-weight-bolder mb-3">
                  {settingsError
                    ? "We couldn't reach the server. Please try again later."
                    : "Sorry! Registration is currently closed."}
                </h6>
                <a className="btn btn-round btn-outline-dark mb-0" href="/">
                  Back to login
                  <i className="fas fa-arrow-right text-sm ms-1" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </Card>
        )}
        <AuthFooter />
      </Col >

      <div className="col-6 d-lg-flex d-none h-100 my-auto pe-0 position-absolute top-0 end-0 text-left flex-column">
        <div className="position-relative bg-gradient-dark h-100 m-3 border-radius-lg d-flex flex-column overflow-hidden" >
          <AuthAnimation />
        </div >
      </div >
    </>
  );
};

export default Register;
